import { IMAGE_WIDTHS, LQIP_WIDTH, WEBP_QUALITY } from './image-paths'

export interface BrowserImageVariant {
  width: number
  base64: string
}

export interface BrowserProcessedImage {
  variants: BrowserImageVariant[]
  lqip: string
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to decode image'))
    img.src = src
  })
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result)
      // Strip the "data:...;base64," prefix
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

async function encodeWebpAt(img: HTMLImageElement, maxWidth: number, quality: number): Promise<Blob> {
  const scale = Math.min(1, maxWidth / img.naturalWidth)
  const w = Math.max(1, Math.round(img.naturalWidth * scale))
  const h = Math.max(1, Math.round(img.naturalHeight * scale))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  ctx.drawImage(img, 0, 0, w, h)
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas WebP encode failed'))),
      'image/webp',
      quality,
    )
  })
}

export async function processImageInBrowser(dataUrl: string): Promise<BrowserProcessedImage> {
  const img = await loadImage(dataUrl)
  const variants: BrowserImageVariant[] = []
  for (const width of IMAGE_WIDTHS) {
    const blob = await encodeWebpAt(img, width, WEBP_QUALITY / 100)
    variants.push({ width, base64: await blobToBase64(blob) })
  }
  const lqipBlob = await encodeWebpAt(img, LQIP_WIDTH, 0.4)
  const lqipBase64 = await blobToBase64(lqipBlob)
  return {
    variants,
    lqip: `data:image/webp;base64,${lqipBase64}`,
  }
}
